import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const BookReviewContainer = styled.div`
  padding: 6rem 1.5rem 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgb(var(--bg));
  min-height: 100vh;
  font-family: 'Arial', sans-serif;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 10% 0%, rgba(216, 71, 43, 0.12), transparent 40%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #d8472b;
  text-align: center;
  margin-bottom: 1rem;
  line-height: 1.2;
`;

const Author = styled.p`
  font-size: 1.2rem;
  color: rgb(var(--fg) / 0.78);
  margin-bottom: 1rem;
  font-style: italic;
`;

const CoverImage = styled.img`
  width: 100%;
  max-width: 300px;
  max-height: 600px;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 20px 40px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.08);
`;

const DatePublished = styled.p`
  font-size: 1rem;
  color: rgb(var(--gray) / 0.55);
  margin-bottom: 2rem;
`;

const ContentSection = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-bottom: 2rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  padding: 2rem;
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #d8472b;
  border-bottom: 1px solid rgba(216, 71, 43, 0.3);
  padding-bottom: 0.5rem;
`;

const SectionContent = styled.div`
  font-size: 1rem;
  line-height: 1.8;
  color: rgb(var(--fg) / 0.82);
`;

const Quote = styled.blockquote`
  font-style: italic;
  margin: 1.5rem 0;
  padding: 1rem;
  background: rgb(var(--fg) / 0.04);
  border-left: 3px solid #d8472b;
  border-radius: 8px;
  color: rgb(var(--fg) / 0.78);
`;

const ListItem = styled.li`
  margin-bottom: 1rem;
  font-size: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

const OrderedList = styled.ol`
  padding-left: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1200px;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  border: 1px solid rgba(216, 71, 43, 0.3);
  color: rgb(var(--fg) / 0.9);
  padding: 0.4rem 0.7rem;
  border-radius: 12px;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`;

const Button = styled.button`
  display: inline-block;
  margin: 2rem 0;
  padding: 0.9rem 2rem;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.92);
  background: rgb(var(--fg) / 0.06);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  text-align: center;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(216, 71, 43, 0.5);
  }
`;

export const InvisibleWomenMetadata = {
  title: "Invisible Women",
  description: "Exposing Data Bias in a World Designed for Men",
  tags: ['Data Bias', 'Gender', 'Technology', 'Society'],
  author: "Caroline Criado Perez",
  datePublished: "2024-06-20",
  category: "Book Review",
  path: "/book-review/invisible-women",
  coverImage: "/images/invisible_women.jpeg",
};

const InvisibleWomen = () => {
  const navigate = useNavigate();
  const metadata = InvisibleWomenMetadata;

  return (
    <BookReviewContainer>
      <Title>{metadata.title}</Title>
      <Author>by {metadata.author}</Author>
      <CoverImage src={metadata.coverImage} alt={metadata.title} />
      <DatePublished>Date Published: {metadata.datePublished}</DatePublished>
      <TagContainer>
        {metadata.tags.map((tag, index) => (
          <Tag key={index}>{tag}</Tag>
        ))}
      </TagContainer>
      <br />
      <br />
      <ContentSection>
        <SectionTitle>Summary</SectionTitle>
        <SectionContent>
          <p>
            "Invisible Women" exposes the shocking extent of gender bias in a world that has been predominantly designed for men. Caroline Criado Perez uses extensive research and data to show how women are often left out of key areas like urban planning, medical research, and technology design. The book illustrates how this oversight negatively affects women’s lives and calls for a more inclusive approach to data collection and design.
          </p>
          <br />
          <p>
            Through a compelling blend of case studies and hard data, Perez challenges the default assumption that men represent the ‘universal’ human experience, arguing that failing to consider women in data leads to policies and products that disadvantage half the population.
          </p>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Key Themes or Concepts</SectionTitle>
        <SectionContent>
          <OrderedList>
            <ListItem><strong>Data Bias:</strong> The book uncovers how data collection methodologies often ignore women’s experiences, leading to biased outcomes.</ListItem>
            <ListItem><strong>Gender Gap:</strong> Perez highlights the disparity between men’s and women’s access to health, technology, and resources due to biased systems.</ListItem>
            <ListItem><strong>Workplace Inequality:</strong> The gender bias in how workplaces are structured and how this affects women’s productivity and safety.</ListItem>
            <ListItem><strong>Urban Planning:</strong> How cities are designed around men’s needs, leading to challenges for women, particularly in terms of safety and accessibility.</ListItem>
            <ListItem><strong>Health and Safety:</strong> The consequences of using male-centered data in medical research and safety testing.</ListItem>
          </OrderedList>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Favorite Quotes</SectionTitle>
        <SectionContent>
          <Quote>
            "The lives of men have been taken to represent those of humans overall."
          </Quote>
          <Quote>
            "When we exclude half of humanity from the data, we are only telling half the story."
          </Quote>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Personal Reflection</SectionTitle>
        <SectionContent>
          <p>
            "Invisible Women" opened my eyes to the pervasive nature of gender bias in everyday life, from the design of office spaces to the technologies we use daily. The book made me realize that while much progress has been made for gender equality, data collection still severely underrepresents women, leading to systemic issues in health, safety, and access to opportunities.
          </p>
          <br />
          <p>
            One of the most striking takeaways for me was how gender data gaps aren’t just inconvenient—they can be life-threatening, particularly in areas like healthcare and safety. The stories and studies Perez presents are both shocking and illuminating.
          </p>
          <br />
          <p>
            As someone interested in data science, "Invisible Women" has reinforced the importance of inclusivity in data collection and analysis. It’s not just about equality; it’s about designing systems that work for everyone.
          </p>
        </SectionContent>
      </ContentSection>

      <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
    </BookReviewContainer>
  );
};

export default InvisibleWomen;
